import React, { useState } from 'react';
import { X } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { IntegrationLogo } from './IntegrationLogo';

/**
 * Collects a per-venue API key before connecting an API-key provider
 * (Klaviyo today). Never used for OAuth providers (Dynamics) — those
 * redirect straight to the provider's consent screen, see
 * connectIntegration() in api/integrations.js.
 *
 * The key is sent as-is to POST /integrations/{provider}/connect, which
 * validates it against the provider's live API before marking the row
 * 'connected' — a wrong or revoked key surfaces here as `error` from
 * onSubmit, not a silent success.
 */
export function ConnectApiKeyModal({ item, onClose, onSubmit }) {
  const [apiKey, setApiKey] = useState('');
  const [accountLabel, setAccountLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!apiKey.trim()) {
      setError('API key is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        apiKey: apiKey.trim(),
        accountLabel: accountLabel.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to connect.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-[0_24px_64px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <IntegrationLogo logoKey={item.logoKey} name={item.name} src={item.logoUrl} size="sm" />
            <div>
              <h3 className="font-display text-sm font-bold text-[#201C35]">
                Connect {item.name}
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                This venue's own API key — validated before it's saved.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div>
            <label htmlFor="api-key" className="text-xs font-semibold text-slate-700">
              API key
            </label>
            <input
              id="api-key"
              type="password"
              autoComplete="off"
              autoFocus
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="pk_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[var(--brand-primary,#1A428A)] focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/15"
            />
          </div>

          <div>
            <label htmlFor="account-label" className="text-xs font-semibold text-slate-700">
              Label (optional)
            </label>
            <input
              id="account-label"
              type="text"
              value={accountLabel}
              onChange={(event) => setAccountLabel(event.target.value)}
              placeholder="e.g. Parken Stadium"
              className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[var(--brand-primary,#1A428A)] focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/15"
            />
          </div>

          {error ? (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="inline-flex h-9 items-center rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={cn(
                'inline-flex h-9 items-center rounded-full px-4 text-sm font-semibold text-white',
                'bg-[var(--brand-primary,#1A428A)] hover:bg-[#163872] disabled:opacity-50'
              )}
            >
              {submitting ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}